import React, { useEffect } from 'react'
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Grid } from '@mui/material'
import { FieldValues, useForm } from 'react-hook-form';
import AppMultiSelect from '../../../../Component/Common/Components/FormValidationInputs/AppMultiSelect';
import AppSelectList, {
    IDropDownOptions
} from '../../../../Component/Common/Components/FormValidationInputs/AppSelectList';
import AppTextInput from '../../../../Component/Common/Components/FormValidationInputs/AppTextInput';
import AppYesOrNoSelector from '../../../../Component/Common/Components/FormValidationInputs/AppYesOrNoSelector';
import { IntermediateGlobalLoading } from '../../../../Component/Common/Components/Loading/IntermediateGlobalLoading';
import { PortalDocumentsDropZone } from '../../../Common/PortalDocuments/PortalDocumentsDropZone';
import { MDTypography } from '../../../../Component/Common/Components/MDTypography'
import {
    Rule,
    ServiceRequiredAndOwnershipStructureForm,
    ServiceRequiredRenderedStructureForm,
    ServiceRequiredRenderedValidationSchema,
    ServiceRequiredValidationSchema
} from './ServiceRequiredValidationSchema';
import { CoreStaticDataService } from '../../../../Component/Api/StaticData/CoreStaticDataService';
import { useRecoilValue } from 'recoil';
import { VerificationCompanyAtom } from '../../../../State/VerificationCompany.Atom';
import {
    VerificationAgencyClientApplicationService
} from '../../../../Component/Api/VerificationAgency/VerificationAgencyClientApplication/VerificationAgencyClientApplicationService';
import { toast } from 'react-toastify';
import {
    ServiceRequiredMatrix,
    VerificationAgencyClientApplication
} from '../../../../Component/Model/Dto/VerificationAgency/VerificationAgencyClientApplication';
import { MDBox } from '../../../../Component/Common/Components/MDBox';
import { CoreEnumLookupDto } from "../../../../Component/Model/Dto/CoreStaticData";
import { OwnershipStructureElement, ScorecardTypeCategory, SectorCode, Service } from "../../../../Component/Model/Enum/BEE123Enums";
import {
    BEEScorecardUserAccessService
} from "../../../../Component/Api/BBEScorecardUserAccess/BEEScorecardUserAccessService";
import { BEE123UserTypes } from "../../../../Component/Common/Constants/Lists/Users";
import { AppDatepicker } from '../../../../Component/Common/Components/FormValidationInputs/AppDatepicker';
import { DataLookupService } from "../../../../Component/Api/DataLookup/DataLookupService";
import { getIOptionsArray } from "../../../../Helpers/arrayHelper";
import { isNil, isEmpty } from "lodash";

export const ServiceRequired = () => {
    const verificationCompany = useRecoilValue(VerificationCompanyAtom);
    const [serviceRequiredOptions, setServiceRequiredOptions] = React.useState<IDropDownOptions[]>([]);
    const [BBBEECodeOptions, setBBBEECodeOptions] = React.useState<IDropDownOptions[]>([]);
    const [sectorCodeOptions, setSectorCodeOptions] = React.useState<IDropDownOptions[]>([]);
    const [sectorCodeMatrix, setSectorCodeMatrix] = React.useState<ServiceRequiredMatrix>({});
    const [scorecardTypeOptions, setScorecardTypeOptions] = React.useState<IDropDownOptions[]>([]);
    const [ownershipStructureElementsOptions, setOwnershipStructureElementsOptions] = React.useState<IDropDownOptions[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [serviceRequiredId, setServiceRequiredId] = React.useState(-1);
    const [ownershipStructureId, setOwnershipStructureId] = React.useState(-1);
    const [renderedStructureId, setRenderedStructureId] = React.useState(0);
    const [applicationReferenceNumber, setApplicationReferenceNumber] = React.useState("");
    const [enquiryDate, setEnquiryDate] = React.useState(new Date());
    const [application, setApplication] = React.useState<VerificationAgencyClientApplication.VerificationAgencyClientApplicationResponse>();
    const [showScorecardType, setShowScorecardType] = React.useState(false);
    const [showNumberOfEntities, setShowNumberOfEntities] = React.useState(false);
    const [disableOwnershipSelection, setDisableOwnershipSelection] = React.useState(false);
    const [disableSubSector, setDisableSubSector] = React.useState(false);
    const [analysts, setAnalysts] = React.useState<IDropDownOptions[]>([]);
    const [technicalSignatory, setTechnicalSignatory] = React.useState<IDropDownOptions[]>([]);
    const [assignedEmployees, setAssignedEmployees] = React.useState<IDropDownOptions[]>([]);
    const [availableScorecards, setAvailableScorecards] = React.useState<IDropDownOptions[]>([]);
    const [applyRule, setApplyRule] = React.useState<Rule>();
    const [disableClaimingBlackOwnership, setDisableClaimingBlackOwnership] = React.useState(false);

    const link = 'ServiceRequired';

    const { control, handleSubmit, formState: { isSubmitting, isDirty }, reset, watch, setValue, getValues } = useForm({
        mode: 'all',
        resolver: yupResolver(ServiceRequiredValidationSchema),
    });

    const { control: controlRendered, handleSubmit: handleRendered, formState: { isSubmitting: isSubmittingRendered }, watch: watchRendered, reset: resetRendered, setValue: setRenderedValue, getValues: getRenderedValues } = useForm({
        mode: 'all',
        resolver: yupResolver(ServiceRequiredRenderedValidationSchema),
        context: {
            rule: applyRule
        }
    });

    const selectedServiceRequired = watch('serviceRequired');
    const selectedBBBEECodes = watch('bbbeeCode');
    const selectedSectorCodes = watch('sectorCode')
    const selectedScorecardType = watch('scorecardTypeCategory');
    const selectedOwnershipStructureElements = watch('ownershipStructureElements');

    const showRecordsCentralised = !isNil(watch('isPartOfGroupStructure', null));
    const showClaimingBlackOwnership = !isNil(watch('isRecordsCentralised', null));
    const showStructureElements = !isNil(watch('claimingBlackOwnership', null));
    const linkedScorecard = watchRendered('linkedScorecard');

    const startDate = watchRendered('startDate');
    const completionDate = watchRendered('completionDate');

    const resetFormData = (data: VerificationAgencyClientApplication.ServiceRequiredAndOwnershipStructureDto) => {
        const formData = {
            applicationReferenceNumber: data.applicationReferenceNumber,
            dateEnquiryReceived: data.dateEnquiryReceived,
            showNumberOfEntities: data.serviceRequired?.scorecardTypeCategory === ScorecardTypeCategory.ConsolidatedGeneric || data.serviceRequired?.scorecardTypeCategory === ScorecardTypeCategory.ConsolidatedQSE,
            ...data.ownershipStructure,
            ...data.serviceRequired
        }
        setApplicationReferenceNumber(data.applicationReferenceNumber);
        setEnquiryDate(data.dateEnquiryReceived);
        setOwnershipStructureId(data.ownershipStructure?.ownershipStructureId ?? -1);
        setServiceRequiredId(data.serviceRequired?.serviceRequiredId ?? -1);
        setShowScorecardType(data.serviceRequired?.sectorCode === 0 ? true : !!data.serviceRequired?.sectorCode);
        reset(formData);
    }

    const resetRenderedFormData = (data: VerificationAgencyClientApplication.ServiceRenderedDto) => {
        setRenderedStructureId(data.renderedStructureId);
        resetRendered(data);
    }

    const handleDataSubmit = async (data: FieldValues) => {
        try {
            const formData = { ...data } as ServiceRequiredAndOwnershipStructureForm;
            const requestData = {
                vaClientApplicationId: verificationCompany.applicationId,
                dateEnquiryReceived: enquiryDate,
                applicationReferenceNumber: applicationReferenceNumber,
                serviceRequired: {
                    serviceRequiredId: serviceRequiredId,
                    serviceRequired: formData.serviceRequired,
                    bbbeeCode: formData.bbbeeCode,
                    sectorCode: formData.sectorCode,
                    scorecardTypeCategory: formData.scorecardTypeCategory,
                    numberOfScorecards: formData.numberOfScorecards,
                    isPartOfGroupStructure: formData.isPartOfGroupStructure,
                    isRecordsCentralised: formData.isRecordsCentralised,
                    numberOfConsolidatedScorecards: formData.numberOfConsolidatedScorecards
                },
                ownershipStructure: {
                    ownershipStructureId: ownershipStructureId,
                    claimingBlackOwnership: formData.claimingBlackOwnership,
                    ownershipStructureElements: formData.ownershipStructureElements,
                    containTrustBboEsop: formData.containTrustBboEsop,
                    percentageBlackOwnershipClaimed: formData.percentageBlackOwnershipClaimed
                },
            } as VerificationAgencyClientApplication.ServiceRequiredAndOwnershipStructureDto;

            if (serviceRequiredId < 0 && ownershipStructureId < 0) {
                const response = await VerificationAgencyClientApplicationService.CreateServiceRequiredAndOwnershipStructure(requestData);
                resetFormData(response.data);
            }
            else {
                const response = await VerificationAgencyClientApplicationService.UpdateServiceRequiredAndOwnershipStructure(requestData);
                resetFormData(response.data);
            }

            toast.success("Service Required Saved");
        } catch (error) {
        }
    }

    const handleRenderedDataSubmit = async (data: FieldValues) => {
        try {
            const formData = { ...data } as ServiceRequiredRenderedStructureForm;

            const requestData = {
                renderedStructureId: renderedStructureId,
                serviceRequiredId: serviceRequiredId,
                linkedScorecard: formData.linkedScorecard,
                verificationAnalyst: formData.verificationAnalyst,
                technicalSignatory: formData.technicalSignatory,
                assignedEmployees: formData.assignedEmployees,
                startDate: formData.startDate,
                completionDate: formData.completionDate,
                notes: formData.notes
            } as VerificationAgencyClientApplication.ServiceRenderedDto;

            await VerificationAgencyClientApplicationService.SaveServiceRequiredRendered(requestData);
            await fetchServiceRequiredRenderedData();

            if (isDirty) {
                await handleDataSubmit(getValues());
            }

            toast.success("Service Required Rendered Saved");
        } catch (error) {
        }
    }

    const fetchStaticData = async () => {
        try {
            const serviceRequiredPromise = CoreStaticDataService.getServicesRequired();
            const BBBEECodesPromise = CoreStaticDataService.getBBBEECodes();
            const scorecardTypesPromise = CoreStaticDataService.getScorecardTypeCategories();
            const ownershipStructureElementsPromise = CoreStaticDataService.getOwnershipStructureElements();
            const sectorCodeMatrixPromise = CoreStaticDataService.getServiceRequiredCodeMatrix();

            const [serviceRequired, BBBEECodes, scorecardTypes, ownershipStructureElements, sectorCodeMatrixElements] = await Promise.all([
                serviceRequiredPromise,
                BBBEECodesPromise,
                scorecardTypesPromise,
                ownershipStructureElementsPromise,
                sectorCodeMatrixPromise
            ]);

            const serviceData: IDropDownOptions[] = serviceRequired.data.map(item => {
                return { id: item.id, value: item.name }
            });
            const BBBEECodesData: IDropDownOptions[] = BBBEECodes.data.map(item => {
                return { id: item.id, value: item.name }
            });

            const scorecardTypeData: IDropDownOptions[] = scorecardTypes.data.map(item => {
                return { id: item.id, value: item.name }
            });
            const ownershipStructureElementsData: IDropDownOptions[] = ownershipStructureElements.data.map(item => {
                return { id: item.id, value: item.name }
            });

            setServiceRequiredOptions(serviceData);
            setBBBEECodeOptions(BBBEECodesData);
            setSectorCodeMatrix(sectorCodeMatrixElements.data);
            setScorecardTypeOptions(scorecardTypeData);
            setOwnershipStructureElementsOptions(ownershipStructureElementsData);
        } catch (error) {
        }
    }

    const handleBeeCodesChange = (selectedValue: number) => {
        if (selectedValue && !isEmpty(sectorCodeMatrix)) {
            try {
                let filteredCodes = sectorCodeMatrix[selectedValue] as unknown as CoreEnumLookupDto[];

                const sectorCodesData: IDropDownOptions[] = filteredCodes.map(item => {
                    return { id: item.id, value: item.name };
                });

                setSectorCodeOptions(sectorCodesData);

                if (sectorCodesData.some(item => item.id === 0)) {
                    setValue('sectorCode', 0, { shouldValidate: true });
                    setDisableSubSector(true)
                }
                else {
                    setDisableSubSector(false)
                }

                setShowScorecardType(true);
            }
            catch (error) {
                setSectorCodeOptions([]);
                setShowScorecardType(false);
            }
        }
        else {
            setSectorCodeOptions([]);
            setShowScorecardType(false);
        }
    }

    const handleServiceRequiredCodesChange = (selectedValue: number) => {
        if (selectedValue === Service.Training || selectedValue === Service.Opinion) {
            setApplyRule(Rule.AssignEmployee);
            setRenderedValue('verificationAnalyst', undefined);
            setRenderedValue('technicalSignatory', undefined);
            setRenderedValue('linkedScorecard', undefined);
        }
        else if (selectedValue === Service.BBBEEVerification) {
            setApplyRule(Rule.LinkScorecard);
            setRenderedValue('assignedEmployees', []);
        }
        else {
            setApplyRule(Rule.OnlyAnalystAndTechnical);
            setRenderedValue('linkedScorecard', undefined);
            setRenderedValue('assignedEmployees', []);
        }
    }
    const handleSectorCodesChange = (selectedValue: number) => {
        //Behavioural - Only apply these rules if ServiceRequired is BEEVerification.
        //It needs to switch back to Link if a 'relevant' sector code is chosen
        if (selectedServiceRequired === Service.BBBEEVerification) {
            if (selectedValue === SectorCode.PropertyAPUTSPropertyUnitTrusts ||
                selectedValue === SectorCode.PropertyEstateAgents ||
                selectedValue === SectorCode.PropertyAPUTSPropertyUnitTrusts ||
                selectedValue === SectorCode.FSCTradeAssociationSpecialisedEnterprises ||
                selectedValue === SectorCode.TransportBusCommuterAndCoach ||
                selectedValue === SectorCode.TransportTaxiOwners ||
                selectedValue === SectorCode.TransportTaxiAssociation ||
                selectedValue === SectorCode.TransportRoadFreight ||
                selectedValue === SectorCode.TransportPublicDOT ||
                selectedValue === SectorCode.TransportPublicSOE ||
                selectedValue === SectorCode.TransportMaritime ||
                selectedValue === SectorCode.TransportForwardingAndClearing ||
                selectedValue === SectorCode.TransportRail ||
                selectedValue === SectorCode.TransportDomesticAviation) {
                setApplyRule(Rule.OnlyAnalystAndTechnical);
                setRenderedValue('linkedScorecard', undefined);
            }
            else {
                setApplyRule(Rule.LinkScorecard);
            }
        }
    }
    const handleLinkedScorecardChange = async (selectedValue?: string) => {
        const notAvailable: IDropDownOptions[] = [
            { value: 'No users found', id: 0 },
        ];
        const analystUsers = await BEEScorecardUserAccessService.getAllScorecardUserAccessByType(BEE123UserTypes.User, selectedValue);
        const technicalUsers = await BEEScorecardUserAccessService.getAllScorecardUserAccessByType(BEE123UserTypes.SubAdmin, selectedValue);

        const analystUsersOptions = getIOptionsArray(analystUsers.data, "id", "name");
        const technicalUsersOptions = getIOptionsArray(technicalUsers.data, "id", "name");

        const analystOptions = analystUsersOptions.length === 0 ? notAvailable : analystUsersOptions;
        const technicalOptions = technicalUsersOptions.length === 0 ? notAvailable : technicalUsersOptions;

        setAnalysts(analystOptions);
        setTechnicalSignatory(technicalOptions);

        if (analystUsersOptions.length === 0) {
            setRenderedValue('verificationAnalyst', 0);
        }
        else if (!analystOptions.some(f => f.id === getRenderedValues('verificationAnalyst'))) {
            setRenderedValue('verificationAnalyst', undefined);
        }

        if (technicalUsersOptions.length === 0) {
            setRenderedValue('technicalSignatory', 0);
        }
        else if (!technicalOptions.some(f => f.id === getRenderedValues('technicalSignatory'))) {
            setRenderedValue('technicalSignatory', undefined);
        }
    }

    useEffect(() => {
        handleServiceRequiredCodesChange(selectedServiceRequired);
    }, [selectedServiceRequired]);

    useEffect(() => {
        handleBeeCodesChange(selectedBBBEECodes);
    }, [selectedBBBEECodes, sectorCodeMatrix]);

    useEffect(() => {
        handleSectorCodesChange(selectedSectorCodes);
    }, [selectedSectorCodes]);

    useEffect(() => {
        if (selectedScorecardType === ScorecardTypeCategory.ConsolidatedGeneric || selectedScorecardType === ScorecardTypeCategory.ConsolidatedQSE) {
            setShowNumberOfEntities(true);
        }
        else {
            setShowNumberOfEntities(false);
            setValue('numberOfConsolidatedScorecards', undefined)
        }
    }, [selectedScorecardType]);

    useEffect(() => {
        let ownershipSelection = getValues('ownershipStructureElements');

        if (ownershipSelection?.some((f: number) =>
            f === OwnershipStructureElement.BroadBasedOwnershipScheme ||
            f === OwnershipStructureElement.EmployeeShareOwnershipProgram ||
            f === OwnershipStructureElement.Trust)) {
            setValue('containTrustBboEsop', true);
            setDisableOwnershipSelection(true);
        } else {
            setDisableOwnershipSelection(false);
        }
    }, [selectedOwnershipStructureElements]);

    useEffect(() => {
        const selectedServiceRequired = watch('serviceRequired');
        if (selectedServiceRequired === Service.OwnershipReview || selectedServiceRequired === Service.OwnershipCertificate) {
            setValue('claimingBlackOwnership', true);
            setDisableClaimingBlackOwnership(true);
        } else {
            setDisableClaimingBlackOwnership(false);
        }
    }, [watch('serviceRequired')]);

    const fetchApplicationServiceRequiredData = async () => {
        try {
            const applicationId = verificationCompany.applicationId;
            const response = await VerificationAgencyClientApplicationService.GetServiceRequiredAndOwnershipStructureByApplicationId(applicationId);
            handleServiceRequiredCodesChange(response.data.serviceRequired?.serviceRequired ?? 0);
            handleSectorCodesChange(response.data.serviceRequired?.sectorCode ?? 0);

            resetFormData(response.data);
        } catch (error) {
        }
    }

    const fetchServiceRequiredRenderedData = async () => {
        try {
            const response = await VerificationAgencyClientApplicationService.GetServiceRequiredRenderedById(serviceRequiredId);
            resetRenderedFormData(response.data);
            await handleLinkedScorecardChange(response.data.linkedScorecard);
        } catch (error) {
        }
    }

    const fetchApplication = async () => {
        try {
            const response = await VerificationAgencyClientApplicationService.GetById(verificationCompany.applicationId);
            setApplication(response.data);
        } catch (error) {
            console.log(error);
        }
    }

    const fetchRenderedDetails = async () => {
        try {
            const availableScorecards = await VerificationAgencyClientApplicationService.GetAvailableScorecardsForServiceRequired();
            const assignedUsers = await DataLookupService.GetUsersInRegistrationList();

            const assignedUsersOptions = getIOptionsArray(assignedUsers.data, "Id", "Name");
            const scorecardOptions = getIOptionsArray(availableScorecards.data, "scorecardId", "scorecardName");

            setAvailableScorecards(scorecardOptions);
            setAssignedEmployees(assignedUsersOptions);

        } catch (error) {
        }
    }

    const fetchData = async () => {
        setLoading(true);
        await fetchStaticData();
        await fetchRenderedDetails();

        if (verificationCompany.applicationId !== -1) {
            const applicationPromise = fetchApplication();
            const serviceRequiredPromise = fetchApplicationServiceRequiredData();
            await Promise.all([applicationPromise, serviceRequiredPromise]);
        }
        setLoading(false);
    }

    useEffect(() => {
        fetchData();
    }, [verificationCompany.applicationId]);

    useEffect(() => {
        if (serviceRequiredId > 0) {
            fetchServiceRequiredRenderedData();
        }
    }, [serviceRequiredId]);

    useEffect(() => {
        if(linkedScorecard) {
            handleLinkedScorecardChange(linkedScorecard);
        }
    }, [linkedScorecard]);

    if (loading) {
        return (<IntermediateGlobalLoading />);
    }
    return (
        <>
            <MDBox style={{ marginBottom: "10px" }}>
                <MDTypography variant='h4' fontWeight='bold'>
                    Application - {application?.referenceNumber}
                </MDTypography>
            </MDBox>
            <Grid container spacing={2}>
                <Grid item component="form" id="ServiceRequireForm" onSubmit={handleSubmit(handleDataSubmit)} xs={6}>
                    <Box>
                        <MDTypography graphy color='primary' variant='h6' fontWeight='bold' mb={1}>
                            Service Required
                        </MDTypography>
                        <Grid container spacing={1.5}>
                            <Grid item xs={10}>
                                <AppSelectList control={control} name="serviceRequired" label="Service Required" items={serviceRequiredOptions} size="small" />
                            </Grid>
                            {watch('serviceRequired') &&
                                <Grid item xs={10}>
                                    <AppSelectList control={control} name="bbbeeCode" label="B-BBEE Codes to Apply" items={BBBEECodeOptions} size="small" />
                                </Grid>}
                            {selectedBBBEECodes &&
                                <Grid item xs={10}>
                                    <AppSelectList control={control} name="sectorCode" label="Sector Codes to Apply" items={sectorCodeOptions} size="small" disabled={disableSubSector} />
                                </Grid>}
                            {showScorecardType &&
                                <Grid item xs={10}>
                                    <AppSelectList control={control} name="scorecardTypeCategory" label="Type of Scorecard Required" items={scorecardTypeOptions} size="small" />
                                </Grid>}
                            {showNumberOfEntities && <Grid item xs={10}>
                                <AppTextInput control={control} name="numberOfConsolidatedScorecards" label="Number of entities" size="small" />
                            </Grid>}
                            {watch('scorecardTypeCategory') && <Grid item xs={10}>
                                <AppYesOrNoSelector control={control} name="isPartOfGroupStructure" label='Is the company part of a group structure?' size="small" />
                            </Grid>}
                            {showRecordsCentralised && <Grid item xs={10}>
                                <AppYesOrNoSelector control={control} name="isRecordsCentralised" label='Are all records/functions centralised?' size="small" />
                            </Grid>}
                            {showClaimingBlackOwnership && <Grid item xs={10}>
                                <AppYesOrNoSelector control={control} name="claimingBlackOwnership" label='Claiming Black Ownership?' size="small" disabled={disableClaimingBlackOwnership}/>
                            </Grid>}
                            {watch('claimingBlackOwnership') && showClaimingBlackOwnership && <Grid item xs={10}>
                                <AppTextInput control={control} name="percentageBlackOwnershipClaimed" label="Percentage Black Ownership Claimed" size="small" />
                            </Grid>}
                            {watch('claimingBlackOwnership') && showStructureElements && showClaimingBlackOwnership && <Grid item xs={10}>
                                <AppMultiSelect control={control} name="ownershipStructureElements" label="Select all elements included in the ownership structure" options={ownershipStructureElementsOptions} size="small" />
                            </Grid>}
                            {disableOwnershipSelection && watch('claimingBlackOwnership') && watch('ownershipStructureElements') && <Grid item xs={10}>
                                <AppYesOrNoSelector control={control} name="containTrustBboEsop" label="Does the Ownership Structure contain Trusts/BBOS's or ESOP's?" size="small" disabled={disableOwnershipSelection} />
                            </Grid>}
                        </Grid>
                    </Box>
                    <Box mt={2}>
                        {serviceRequiredId > 0 ? null : (
                            <LoadingButton loading={isSubmitting} variant='contained' form='ServiceRequireForm' type="submit" color='primary'>
                                Save
                            </LoadingButton>
                        )}
                    </Box>
                </Grid>
                {serviceRequiredId > 0 && <Grid item component="form" id="ServiceRenderedForm" onSubmit={handleRendered(handleRenderedDataSubmit)} xs={6}>
                    <Box>
                        <MDTypography graphy color='primary' variant='h6' fontWeight='bold' mb={1}>
                            Rendered
                        </MDTypography>
                        <Grid container spacing={1.5}>
                            {!watchRendered('scorecardName') && applyRule === Rule.LinkScorecard && <Grid item xs={10}>
                                <AppSelectList control={controlRendered} name="linkedScorecard" label="Link Scorecard" items={availableScorecards} size="small" />
                            </Grid>}
                            {watchRendered('scorecardName') && applyRule === Rule.LinkScorecard && <Grid item xs={10}>
                                <AppTextInput control={controlRendered} name="scorecardName" label="Linked Scorecard" size="small" disabled={true} />
                            </Grid>}
                            {(applyRule === Rule.LinkScorecard || applyRule === Rule.OnlyAnalystAndTechnical) &&
                                <Grid item xs={10}>
                                    <AppSelectList control={controlRendered} name="verificationAnalyst" label="Verification Analyst" items={analysts} size="small" />
                                </Grid>}
                            {(applyRule === Rule.LinkScorecard || applyRule === Rule.OnlyAnalystAndTechnical) && <Grid item xs={10}>
                                <AppSelectList control={controlRendered} name="technicalSignatory" label="Technical Signatory" items={technicalSignatory} size="small" />
                            </Grid>}

                            {applyRule === Rule.AssignEmployee && <Grid item xs={10}>
                                <AppMultiSelect control={controlRendered} name="assignedEmployees" label="Assigned Employee(s)" options={assignedEmployees} size="small" />
                            </Grid>}
                            <Grid container item xs={10} spacing={1.5}>
                                <Grid item xs={6}>
                                    <AppDatepicker control={controlRendered} name="startDate" label="Start Date" maxDate={completionDate} inputFormat='yyyy/MM/dd' />
                                </Grid>
                                <Grid item xs={6}>
                                    <AppDatepicker control={controlRendered} name="completionDate" minDate={startDate} label="Completion Date" inputFormat='yyyy/MM/dd' />
                                </Grid>
                            </Grid>
                            <Grid item xs={10}>
                                <AppTextInput control={controlRendered} label='Notes' name='notes' multiline={true}></AppTextInput>
                            </Grid>
                            <Grid item xs={10}>
                                <PortalDocumentsDropZone id={serviceRequiredId} link={link} />
                            </Grid>
                        </Grid>
                    </Box>
                    <Box mt={2}>
                        <LoadingButton loading={isSubmittingRendered} variant='contained' form='ServiceRenderedForm' type="submit" color='primary'>
                            Save
                        </LoadingButton>
                    </Box>
                </Grid>}
            </Grid>
        </>
    )
}
